import { UseAppFormProps } from "hooks/useAppForm";
import { Component, FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import AdminEditor from "./../AdminEditor";
import moment, { Moment } from "moment";
import MonacoEditor, { OnMount } from "@monaco-editor/react";
import AppAlert from "./../AppAlert";
import * as monaco from "monaco-editor";
import AppUploadImage, { AppUploadImageProps } from "./../AppUploadImage";
import Select from "./Select";


interface AppFormProps<T> {
    id?: string;
    form: UseAppFormProps<T>
    onSubmit: (values: T) => void;
    children?: ReactNode;
    cols?: 1 | 2 | 3 | 4;
    noStyle?: boolean;
    shadow?: boolean;
    className?: string;
}
const Form = <T extends object>({
    id,
    form,
    onSubmit,
    children,
    cols,
    noStyle,
    shadow = true,
    ...props
}: AppFormProps<T>) => {

    const { validateForm, values } = form;
    const className = useMemo(() => {
        let cls = noStyle ? `${props.className} ` : `bg-white p-4 rounded ${props.className} `;
        if (shadow) {
            cls += " shadow "
        }
        switch (cols) {
            case 2:
                return cls + "grid grid-cols-1 md:grid-cols-2 gap-x-4 w-full ";
            case 3:
                return cls + "grid grid-cols-1 md:grid-cols-3 gap-x-4 w-full ";
            case 4:
                return cls + "grid grid-cols-1 md:grid-cols-4 gap-x-4 w-full ";
            default:
                return cls + "w-full";

        }
    }, [cols, noStyle, props.className])
    return <Fragment>
        <form
            id={id}
            ref={form.formRef}
            className={className}
            onSubmit={(e) => {
                e.preventDefault();
                if (validateForm()) {
                    onSubmit(values);
                }
            }}
        >
            {children}
        </form>
    </Fragment>
}
interface ActionProps {
    col?: number;
    children?: ReactNode;
}
const Action: FC<ActionProps> = ({ col, children }) => {
    const className = useMemo(() => {
        if (col) {
            return `flex justify-end space-x-2 md:col-span-${col} mt-2`;
        }
        return "flex justify-end space-x-2 ";
    }, [col])
    return <div className={className}>
        {children}
    </div>
}

export interface ItemProps {
    col?: number;
    label?: ReactNode;
    required?: boolean;
    children?: ReactNode;
    error?: string;
    className?: string;
    itemClassName?: string;
    readOnly?: boolean;
}
export const Item: FC<ItemProps> = ({ col, label, required, children, error, itemClassName, ...props }) => {
    const className = useMemo(() => {
        if (col) {
            return `relative mb-2 md:col-span-${col} ${itemClassName} ${props.className}`;
        }
        return `relative mb-2 ${itemClassName} ${props.className}`;
    }, [col, itemClassName, props.className])
    return <div className={className}>
        {label && <label className="block text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>}
        {children}
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
}

interface InputProps extends ItemProps {
    value?: string;
    onChange?: (value?: string) => void;
    placeholder?: string;
}
const Input: FC<InputProps> = ({ value, onChange, col, error, label, placeholder, required, itemClassName, readOnly }) => {
    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:outline-none focus:border-red-500 focus:ring-red-500`;
        }
        return "w-full border px-4 py-2 rounded focus:outline-none focus:border-blue-500"
    }, [error])
    return <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>
        <input
            readOnly={readOnly}
            value={value || ""}
            onChange={e => onChange && onChange(e.currentTarget.value)}
            type="text"
            className={inputClassName}
            placeholder={placeholder}
        />
    </Item >
}
interface PasswordProps extends ItemProps {
    value?: string;
    onChange?: (value?: string) => void;
    placeholder?: string;
}

const Password: FC<PasswordProps> = ({ value, onChange, col, error, label, placeholder, required, itemClassName }) => {
    const [showPassword, setShowPassword] = useState(false);

    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:outline-none focus:border-red-500 focus:ring-red-500 pr-10`;
        }
        return "w-full border px-4 py-2 rounded focus:outline-none focus:border-blue-500 pr-10";
    }, [error]);

    return (
        <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>
            <div className="relative">
                <input
                    autoComplete="new-password"
                    value={value || ""}
                    onChange={e => onChange && onChange(e.currentTarget.value)}
                    type={showPassword ? "text" : "password"}
                    className={inputClassName}
                    placeholder={placeholder}
                />
                <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                    {showPassword ? (
                        <i className="fas fa-eye-slash"></i>
                    ) : (
                        <i className="fas fa-eye"></i>
                    )}
                </button>
            </div>
        </Item>
    );
};


interface NumberProps extends ItemProps {
    value?: number;
    onChange?: (value?: number) => void;
    placeholder?: string;
}
const Number: FC<NumberProps> = ({ value, onChange, col, error, label, placeholder, required, itemClassName }) => {
    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:border-red-500 focus:ring-red-500 focus:outline-none`;
        }
        return "w-full border px-4 py-2 rounded focus:outline-none focus:border-blue-500"
    }, [error])
    return <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>
        <input
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.currentTarget;
                onChange?.(value ? parseInt(value) : undefined);
            }}
            type="number"
            className={inputClassName}
            placeholder={placeholder}
        />
    </Item >
}

interface TextareaProps extends ItemProps {
    value?: string;
    onChange?: (value?: string) => void;
    placeholder?: string;
    rows?: number;
}
const Textarea: FC<TextareaProps> = ({ value, onChange, col, error, label, placeholder, required, rows, itemClassName }) => {
    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:border-red-500 focus:ring-red-500 focus:outline-none`;
        }
        return "w-full border px-4 py-2 rounded focus:outline-none focus:border-blue-500"
    }, [error])
    return <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>
        <textarea
            value={value || ""}
            onChange={e => onChange && onChange(e.currentTarget.value)}
            className={inputClassName}
            placeholder={placeholder}
            rows={rows}
        />
    </Item >
}

interface CodeEditorProps extends ItemProps {
    value?: string;
    onChange?: (value?: string) => void;
    height?: string | number | undefined;
    language?: string;
}
const CodeEditor: FC<CodeEditorProps> = ({ value, onChange, col, error, label, required, height = 200, language = "html", itemClassName }) => {
    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border rounded border-red-500 focus:border-red-500 focus:ring-red-500 focus:outline-none overflow-hidden`;
        }
        return "w-full border rounded focus:outline-none focus:border-blue-500 overflow-hidden"
    }, [error])

    const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

    const handleMount: OnMount = (editor, monacoInstance) => {
        editorRef.current = editor;

        // bơm type vào Monaco
        monacoInstance.languages.typescript.typescriptDefaults.addExtraLib(
            "ts:filename/formTypes.d.ts"
        );

    };

    return <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>
        <div
            className="relative group rounded"
            style={{ height }}
        >
            <MonacoEditor
                height="100%"
                language={language}
                loading="Vui lòng chờ..."
                value={value || ""}
                onChange={(value, ev) => {
                    // const edits = ev.changes;
                    // for (const edit of edits) {
                    //     const lineNumber = edit.range.startLineNumber;
                    //     console.log(lineNumber, edit.range.endLineNumber, edit.range.endColumn);
                    // }
                    onChange && onChange(value)
                }}
                className={inputClassName}
                options={{
                    tabSize: 4,
                    insertSpaces: false, // true = dùng space, false = dùng ký tự tab
                    fixedOverflowWidgets: true, // giữ popup không bị che
                }}
                onMount={handleMount}
            />
            <button
                type="button"
                onClick={() => AppAlert.modal({
                    title: label || <span>Toàn màn hình</span>,
                    fullWidth: true,
                    fullHeight: true,
                    content: <Fragment>
                        <MonacoEditor
                            key="modal"
                            height="100%"
                            language={language}
                            loading="Vui lòng chờ..."
                            value={value || ""}
                            onChange={value => onChange && onChange(value)}
                            className={inputClassName}
                            options={{
                                tabSize: 4,
                                insertSpaces: false, // true = dùng space, false = dùng ký tự tab
                            }}
                        />
                    </Fragment>,
                })}
                className="absolute -top-5 -right-5 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-80 transition"
            >
                ⛶
            </button>
        </div>
    </Item >
}

interface EditorProps extends ItemProps {
    value?: string;
    onChange?: (value?: string) => void;
}
const Editor: FC<EditorProps> = ({ value, onChange, col, error, label, required, itemClassName }) => {
    return <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>
        <AdminEditor
            value={value || ""}
            onChange={onChange}
        />
    </Item >
}
// AppForm

interface DatePickerProps extends ItemProps {
    value?: string | Date | Moment;
    onChange?: (value?: string) => void;
    placeholder?: string;
    format?: string;
}

export const DatePicker: FC<DatePickerProps> = ({
    value,
    onChange,
    col,
    error,
    label,
    placeholder,
    required,
    format = "DD/MM/YYYY HH:mm",
    itemClassName
}) => {
    const internalValue = useMemo(() => {
        return value ? moment(value, format, true).format(format) : "";
    }, [value, format])
    const [showCalendar, setShowCalendar] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);

    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:border-red-500 focus:ring-red-500 pr-8 focus:outline-none`;
        }
        return "w-full border px-4 py-2 rounded bg-white cursor-pointer pr-8 focus:outline-none focus:border-blue-500";
    }, [error]);

    // Đóng khi click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(e.target as Node)
            ) {
                setShowCalendar(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const clearDate = () => {
        onChange && onChange(undefined);
    };

    return (
        <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>

            <div className="relative group/date-picker">
                <input
                    value={internalValue}
                    readOnly
                    className={inputClassName}
                    placeholder={placeholder || format}
                    onClick={() => setShowCalendar(true)}
                />
                {/* Nút clear */}
                {internalValue && (
                    <span
                        className="absolute w-6 h-6 mr-2 right-8 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 opacity-0 group-hove/date-pickerr:opacity-100 transition-opacity duration-200"
                        onClick={clearDate}
                    >
                        ✕
                    </span>
                )}
                {/* Icon calendar */}
                <span
                    className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
                    onClick={() => setShowCalendar((prev) => !prev)}
                >
                    📅
                </span>
                {showCalendar && <Calendar value={value} onChange={onChange} format={format} onClose={() => setShowCalendar(false)} />}
            </div>
        </Item>
    );
};

interface CalendarProps {
    value?: string | Date | Moment;
    onChange?: (value?: string) => void;
    format?: string;
    onClose?: () => void;
}
export const Calendar: FC<CalendarProps> = ({
    value,
    onChange,
    format = "DD/MM/YYYY HH:mm",
    onClose
}) => {
    const [internalValue, setInternalValue] = useState<string>(
        value ? moment(value, format, true).format(format) : ""
    );
    const calendarRef = useRef<HTMLDivElement>(null);

    const [viewDate, setViewDate] = useState<Moment>(
        moment(value, format, true).isValid()
            ? moment(value, format, true)
            : moment()
    );
    const [hour, setHour] = useState<number>(
        moment(value).isValid() ? moment(value).hour() : 0
    );
    const [minute, setMinute] = useState<number>(
        moment(value).isValid() ? moment(value).minute() : 0
    );
    const hasTime = useMemo(() => /H|h/.test(format), [format]);

    // Đóng khi click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(e.target as Node)
            ) {
                onClose && onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectDate = (day: Moment) => {
        let finalDate = day.clone();
        if (hasTime) {
            finalDate = finalDate.hour(hour).minute(minute);
        }
        const formatted = finalDate.format(format);
        setInternalValue(formatted);
        onChange && onChange(formatted);
        setViewDate(finalDate);
        onClose && onClose();
    };

    const clearDate = () => {
        setInternalValue("");
        setHour(0);
        setMinute(0);
        onChange && onChange(undefined);
    };

    const renderCalendar = () => {
        const startOfMonth = viewDate.clone().startOf("month").startOf("week");
        const endOfMonth = viewDate.clone().endOf("month").endOf("week");
        const days: Moment[] = [];
        let day = startOfMonth.clone();

        while (day.isBefore(endOfMonth)) {
            days.push(day.clone());
            day.add(1, "day");
        }

        const months = moment.months();
        const years = Array.from(
            { length: 101 },
            (_, i) => moment().year() - 50 + i
        );

        return (
            <div
                ref={calendarRef}
                className="absolute z-50 mt-1 bg-white border rounded shadow-lg p-3 w-80"
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                    <button
                        type="button"
                        className="px-2 py-1 hover:bg-gray-200 rounded"
                        onClick={() =>
                            setViewDate(viewDate.clone().subtract(1, "month"))
                        }
                    >
                        ←
                    </button>
                    <div className="flex gap-2">
                        <select
                            value={viewDate.month()}
                            onChange={(e) =>
                                setViewDate(
                                    viewDate.clone().month(parseInt(e.target.value))
                                )
                            }
                            className="border rounded px-1"
                        >
                            {months.map((m, i) => (
                                <option value={i} key={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                        <select
                            value={viewDate.year()}
                            onChange={(e) =>
                                setViewDate(
                                    viewDate.clone().year(parseInt(e.target.value))
                                )
                            }
                            className="border rounded px-1"
                        >
                            {years.map((y) => (
                                <option value={y} key={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        className="px-2 py-1 hover:bg-gray-200 rounded"
                        onClick={() =>
                            setViewDate(viewDate.clone().add(1, "month"))
                        }
                    >
                        →
                    </button>
                </div>

                {/* Weekdays */}
                <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium mb-1">
                    {moment.weekdaysShort().map((d) => (
                        <div key={d}>{d}</div>
                    ))}
                </div>

                {/* Days */}
                <div className="grid grid-cols-7 gap-1 text-center">
                    {days.map((d, idx) => {
                        const isCurrentMonth = d.month() === viewDate.month();
                        const isSelected =
                            moment(internalValue, format, true).isValid() &&
                            d.isSame(
                                moment(internalValue, format, true),
                                "day"
                            );

                        const today = moment();
                        const isToday = d.isSame(today, "day");

                        return (
                            <button
                                type="button"
                                key={idx}
                                onClick={() => selectDate(d)}
                                className={`p-1 rounded ${isToday && !isSelected ?
                                    "bg-blue-100"
                                    : isSelected
                                        ? "bg-blue-500 text-white"
                                        : isCurrentMonth
                                            ? "hover:bg-gray-200"
                                            : "text-gray-400"
                                    }`}
                            >
                                {d.date()}
                            </button>
                        );
                    })}
                </div>

                {/* Time Picker (only if format has time) */}
                {hasTime && (
                    <div className="flex items-center justify-center gap-2 mt-3">
                        <b>Thời gian:</b>
                        <select
                            value={hour}
                            onChange={(e) => setHour(parseInt(e.target.value))}
                            className="border rounded px-1 py-0.5"
                        >
                            {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>
                                    {i.toString().padStart(2, "0")}
                                </option>
                            ))}
                        </select>
                        :
                        <select
                            value={minute}
                            onChange={(e) => setMinute(parseInt(e.target.value))}
                            className="border rounded px-1 py-0.5"
                        >
                            {Array.from({ length: 60 }, (_, i) => (
                                <option key={i} value={i}>
                                    {i.toString().padStart(2, "0")}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        );
    };

    return renderCalendar();
};

interface CheckboxProps extends ItemProps {
    checked?: boolean;
    onChange?: (checked: boolean) => void;
}

const Checkbox: FC<CheckboxProps> = ({ checked, onChange, col, label, required, error, itemClassName }) => {
    const className = useMemo(() => {
        if (col) {
            return `mb-2 ${itemClassName} md:col-span-${col}`;
        }
        return `mb-2 ${itemClassName}`;
    }, [col, itemClassName]);

    return (
        <div className={className}>
            <label className="inline-block space-x-2 cursor-pointer">
                <input
                    type="checkbox"
                    checked={checked || false}
                    onChange={e => onChange?.(e.target.checked)}
                    className={`w-4 h-4 border rounded cursor-pointer ${error ? "border-red-500" : "border-gray-300"
                        }`}
                />
                <span className="text-gray-700">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </span>
            </label>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
    );
};

interface RadioOption {
    label: string;
    value: string;
}

interface RadioProps extends ItemProps {
    value?: string;
    onChange?: (value: string) => void;
    options: RadioOption[];
}

const Radio: FC<RadioProps> = ({ value, onChange, col, error, label, required, options, itemClassName }) => {
    const wrapperClass = useMemo(() => col ? `mb-2 md:col-span-${col}` : "mb-2", [col]);
    return (
        <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>
            <div className={wrapperClass + " flex flex-col space-y-1"}>
                {options.map(opt => (
                    <label key={opt.value} className="inline-flex items-center space-x-2 cursor-pointer">
                        <input
                            type="radio"
                            name={label?.toString() || "radio-group"}
                            value={opt.value}
                            checked={value === opt.value}
                            onChange={() => onChange && onChange(opt.value)}
                            className={`w-4 h-4 border rounded cursor-pointer ${error ? "border-red-500" : "border-gray-300"}`}
                        />
                        <span className="text-gray-700">{opt.label}</span>
                    </label>
                ))}
            </div>
        </Item>
    );
};

interface UploadImageBaseProps extends ItemProps {
    height?: number | string;
    width?: number | string;
    minHeight?: number | string;
    imageClassName?: string;
};

const UploadImage: FC<UploadImageBaseProps & AppUploadImageProps> = (props) => {

    const { col, error, label, required, itemClassName } = props;

    return (
        <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>
            {props.multiple ? (
                <AppUploadImage
                    multiple
                    value={props.value || []}
                    onChange={props.onChange}
                    height={props.height}
                    width={props.width}
                    minHeight={props.minHeight}
                    imageClassName={props.imageClassName}
                />
            ) : (
                <AppUploadImage
                    value={props.value || ""}
                    onChange={props.onChange}
                    height={props.height}
                    width={props.width}
                    minHeight={props.minHeight}
                    imageClassName={props.imageClassName}
                />
            )}
        </Item>
    )
}

class AppForm<T extends object> extends Component<AppFormProps<T>> {
    static Item = Item;
    static Input = Input;
    static Password = Password;
    static Number = Number;
    static Editor = Editor;
    static Select = Select;
    static DatePicker = DatePicker;
    static Textarea = Textarea;
    static Checkbox = Checkbox;
    static Radio = Radio;
    static CodeEditor = CodeEditor;
    static UploadImage = UploadImage;
    static Action = Action;
    render(): ReactNode {
        return <Form<T> {...this.props} />
    }
}
export default AppForm;